import { createContext, useContext, useMemo, useRef, ReactNode } from 'react';
import { SMOOTH_PLAYER_MAX_RESIDENT_PLAYERS } from '../constants';

/**
 * A card-side handle that lets the coordinator forcibly pause the card or
 * tear down its decoding worker. Each `SmoothJumpVideoPlayer` instance
 * registers exactly one of these for the lifetime of its worker.
 */
export interface ResidentHandle {
    /**
     * Forcibly pause the card. Called when another card claims the active
     * playback slot. The card should flip its local play state to paused so
     * its UI reflects the change, but keep its worker alive.
     */
    pause: () => void;
    /**
     * Tear down the decoding worker and reclaim its resources. The card
     * should snapshot its current frame to a still canvas and save its
     * current program time so play() can re-spawn a worker that resumes
     * near where it left off.
     */
    evict: () => void;
}

export interface PlaybackCoordinator {
    /**
     * Claim the single "actively playing" slot. If another card currently
     * holds it, that card's `pause()` is invoked synchronously before this
     * returns. Also bumps LRU for `id`.
     */
    claimActive: (id: string) => void;
    /** Release the active slot if this card holds it. No-op otherwise. */
    releaseActive: (id: string) => void;
    /**
     * Register a resident worker. If the resident set is already at the cap,
     * the least-recently-used resident is evicted first (its `evict()` is
     * invoked synchronously).
     */
    registerResident: (id: string, handle: ResidentHandle) => void;
    /** Remove a resident (e.g. on card unmount or self-eviction). */
    unregisterResident: (id: string) => void;
    /**
     * Mark `id` as most-recently-used in the LRU. Called on every play /
     * seek / interaction so frequently-used cards are not evicted under cap
     * pressure.
     */
    touchResident: (id: string) => void;
    /**
     * Register a lightweight player that only needs mutual-pause behaviour.
     * Unlike `registerResident`, this does NOT count toward the worker cap
     * and is never evicted. Use for players with no expensive decode worker
     * (e.g. video.js / native <video>).
     */
    registerPausable: (id: string, pause: () => void) => void;
    /** Remove a pausable (e.g. on player unmount). */
    unregisterPausable: (id: string) => void;
}

const PlaybackCoordinatorContext = createContext<PlaybackCoordinator | null>(null);

export const usePlaybackCoordinator = (): PlaybackCoordinator => {
    const ctx = useContext(PlaybackCoordinatorContext);
    if (!ctx) {
        throw new Error(
            'usePlaybackCoordinator must be used inside <PlaybackCoordinatorProvider>',
        );
    }
    return ctx;
};

interface ProviderProps {
    children: ReactNode;
    /** Override the global cap from constants/.env. Mainly for tests. */
    maxResidents?: number;
}

/**
 * Holds card-playback coordination state. All state is in refs so updates
 * never trigger a re-render — cards drive their own UI off local state and
 * the coordinator's job is purely to nudge them via callbacks.
 *
 * Invariants:
 *  - `activeIdRef` is either null or a key present in `residentsRef`.
 *  - `residentsRef.size <= max`. Insertion-ordered: oldest entry is LRU.
 */
export const PlaybackCoordinatorProvider = ({ children, maxResidents }: ProviderProps) => {
    const activeIdRef = useRef<string | null>(null);
    // Map preserves insertion order — re-inserting (delete + set) moves a key
    // to "most recent". The first key returned by .keys() is the LRU entry.
    const residentsRef = useRef<Map<string, ResidentHandle>>(new Map());
    // Lightweight players (no decode worker): participate in mutual-pause
    // but never count toward the resident cap and are never evicted.
    const pausablesRef = useRef<Map<string, () => void>>(new Map());

    const api = useMemo<PlaybackCoordinator>(() => {
        const cap = Math.max(1, maxResidents ?? SMOOTH_PLAYER_MAX_RESIDENT_PLAYERS);

        const touch = (id: string) => {
            const r = residentsRef.current;
            const handle = r.get(id);
            if (handle === undefined) return;
            r.delete(id);
            r.set(id, handle);
        };

        const evictLRUWhileOverCap = () => {
            const r = residentsRef.current;
            while (r.size > cap) {
                const it = r.keys().next();
                if (it.done) return;
                const lruId = it.value;
                const handle = r.get(lruId)!;
                // Remove first so re-entrant calls (e.g. the evicted card
                // calling unregisterResident inside evict()) see consistent
                // state.
                r.delete(lruId);
                if (activeIdRef.current === lruId) activeIdRef.current = null;
                try {
                    handle.evict();
                } catch (e) {
                    console.error('[PlaybackCoordinator] evict() threw:', e);
                }
            }
        };

        return {
            claimActive(id) {
                const prev = activeIdRef.current;
                if (prev === id) {
                    touch(id);
                    return;
                }
                if (prev !== null) {
                    const prevHandle = residentsRef.current.get(prev);
                    const prevPause = prevHandle
                        ? () => prevHandle.pause()
                        : pausablesRef.current.get(prev);
                    if (prevPause) {
                        try {
                            prevPause();
                        } catch (e) {
                            console.error('[PlaybackCoordinator] pause() threw:', e);
                        }
                    }
                }
                activeIdRef.current = id;
                touch(id);
            },
            releaseActive(id) {
                if (activeIdRef.current === id) activeIdRef.current = null;
            },
            registerResident(id, handle) {
                const r = residentsRef.current;
                // If already present, replace handle and bump to most-recent.
                if (r.has(id)) r.delete(id);
                r.set(id, handle);
                evictLRUWhileOverCap();
            },
            unregisterResident(id) {
                residentsRef.current.delete(id);
                if (activeIdRef.current === id) activeIdRef.current = null;
            },
            touchResident: touch,
            registerPausable(id, pause) {
                pausablesRef.current.set(id, pause);
            },
            unregisterPausable(id) {
                pausablesRef.current.delete(id);
                if (activeIdRef.current === id) activeIdRef.current = null;
            },
        };
        // Cap is read once at provider mount. Changing it requires remounting
        // the provider — acceptable since it's an env/constants value.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PlaybackCoordinatorContext.Provider value={api}>
            {children}
        </PlaybackCoordinatorContext.Provider>
    );
};
